// The offer builder's funding rule, in one place.
//
// Escrow is taken from the RECEIVER of whichever leg opens, for that leg's full
// value, before anyone ships. So a party's requirement depends on whether they
// move first or second:
//
//   - moving FIRST  → they must front the whole value arriving to them
//   - moving SECOND → the first leg has already paid them, so they only need
//                     the difference between the two sides
//
// Both budgets below are role-dependent, and SYMMETRICALLY so. An earlier
// revision computed the counterparty's requirement as their gross inbound while
// computing the viewer's by role; that asymmetry is the defect that shipped on
// the accept path and it over-states their shortfall by the whole value of the
// first leg (2,156 instead of 43 on the reported case).
//
// No circularity: each budget derives from RAW balances against RAW totals,
// never from the other's result.
//
// This is a CLIENT-SIDE copy of a rule the backend owns authoritatively
// (evaluateOfferCompletability, task 1217649740030687). It exists only because
// live per-keystroke feedback cannot round-trip. The backend stays the
// enforcement point at propose and accept; the shared cases are pinned in
// __tests__/offerCoverage.test.ts and must be mirrored there.

/** The minimum an item must expose for coverage — satisfied by OfferLineItem */
export interface CoverageItem {
  userMTGCardHaveId: number;
  unitPricePoints: number;
  quantity: number;
}

export interface CoverageInput {
  /** The They send column: cards arriving TO the viewer */
  items: CoverageItem[];
  viewerPoints: number;
  counterpartyPoints: number;
  /** Total of the You send column: cards leaving FROM the viewer */
  youSendTotal: number;
}

export interface CoverageResult {
  /** What the viewer can put against the They send column, given who opens */
  coverageBudget: number;
  /** Total of the They send column (line costs summed the same way as the footer) */
  theySendTotal: number;
  viewerShortfall: number;
  counterpartyShortfall: number;
  /** Both conditions hold: a leg can open AND both sides absorb their difference */
  completable: boolean;
  counterpartyCanOpen: boolean;
  viewerCanOpen: boolean;
}

/**
 * Per-line cost. Authoritative because the column footer totals are built the
 * same way (OfferBuilderContext's sideTotal) — computing it any other way here
 * would let the divider disagree with the total the user can see. Note this
 * tracks unitPricePoints, which setItemCondition swaps on a condition change,
 * NOT the original rowQuotedPricePoints.
 */
export const lineCost = (item: CoverageItem): number =>
  item.unitPricePoints * item.quantity;

export const computeCoverage = ({
  items,
  viewerPoints,
  counterpartyPoints,
  youSendTotal,
}: CoverageInput): CoverageResult => {
  const theySendTotal = items.reduce((sum, item) => sum + lineCost(item), 0);

  // Who is able to open a leg by covering their own full inbound value
  const counterpartyCanOpen = counterpartyPoints >= youSendTotal;
  const viewerCanOpen = viewerPoints >= theySendTotal;

  // Whoever goes second is paid by the first leg before they must escrow
  const coverageBudget = counterpartyCanOpen ? viewerPoints + youSendTotal : viewerPoints;
  const counterpartyBudget = viewerCanOpen
    ? counterpartyPoints + theySendTotal
    : counterpartyPoints;

  const viewerShortfall = Math.max(0, theySendTotal - coverageBudget);
  const counterpartyShortfall = Math.max(0, youSendTotal - counterpartyBudget);

  // Condition 1: some leg can open. Condition 2: once both legs settle, neither
  // balance goes below zero. Whoever covers condition 1 satisfies their own
  // condition 2 automatically, so only these two checks are ever needed.
  const someLegCanOpen = viewerCanOpen || counterpartyCanOpen;
  const bothAbsorbDifference =
    viewerPoints >= Math.max(0, theySendTotal - youSendTotal) &&
    counterpartyPoints >= Math.max(0, youSendTotal - theySendTotal);
  const completable = someLegCanOpen && bothAbsorbDifference;

  return {
    coverageBudget,
    theySendTotal,
    viewerShortfall,
    counterpartyShortfall,
    completable,
    counterpartyCanOpen,
    viewerCanOpen,
  };
};

// ---------------------------------------------------------------------------
// Per-column cutoff
// ---------------------------------------------------------------------------

/**
 * The cutoff is a general "whose points run out where" marker, not a me-only
 * concept. Each column is funded by the RECEIVING party's balance:
 *
 *   They send  → cards arrive to the viewer   → the VIEWER's budget funds them
 *   You send   → cards go to the counterparty → THEIR budget funds them
 *
 * So this is parameterized by (items, funder budget) and knows nothing about
 * which side it is drawing.
 */
export interface ColumnCutoffInput {
  items: CoverageItem[];
  /** The receiving party's coverage budget for this column */
  funderBudget: number;
}

export interface ColumnCutoff {
  /** Total value of the column */
  total: number;
  /** How far past the funder's budget this column runs; 0 when fully covered */
  shortfall: number;
  /**
   * The column's items in RENDER order: every fundable card first, then every
   * unfundable one. Callers must render this array, not the input order.
   */
  orderedItems: CoverageItem[];
  /**
   * Index into `orderedItems` where the line is drawn. Everything before it is
   * sendable, everything from it down is not. Undefined when nothing is short.
   */
  boundaryIndex?: number;
  uncoveredIds: number[];
  coveredIds: number[];
  /** Points actually committed to the funded set */
  spent: number;
  /**
   * Cheapest single item whose removal ACTUALLY brings the column under budget,
   * verified by recomputing the total without it. Undefined when no single
   * removal is enough — offering one that leaves the user still short is the
   * defect this field exists to prevent, so it is emitted only when proven.
   */
  removalCandidateId?: number;
  /** Budget left after removing removalCandidateId; never negative */
  surplusAfterRemoval?: number;
}

/**
 * Where the funder's points run out in ONE column.
 *
 * The horizontal line is the feature, and the ordering serves it: cards that
 * can be paid for render ABOVE the line, cards that cannot render BELOW it.
 * That is the whole contract, and it forces two things that are easy to get
 * wrong:
 *
 * 1. The funded set SKIPS cards that do not fit rather than stopping dead at
 *    the first one. A greedy prefix walk would let a single expensive addition
 *    unfund cheaper cards that were perfectly affordable a moment earlier —
 *    adding a 500 to a 120 budget would black out an already-sendable 100. The
 *    skip keeps every still-viable card sendable, which is the point of showing
 *    the line at all.
 *
 * 2. Because the funded set is chosen by value and not by position, the column
 *    is RE-ORDERED for render: funded block first, then unfunded, each by value
 *    descending. Without that the dimmed rows are scattered and no single
 *    horizontal cut can separate them — which is what produced the shipped bug
 *    where covered cards sat below the line and dimmed cards sat above it.
 *
 * Both blocks stay value-descending so the ordering is deterministic and the
 * most valuable sendable card always leads the column.
 */
export const computeColumnCutoff = ({
  items,
  funderBudget,
}: ColumnCutoffInput): ColumnCutoff => {
  const total = items.reduce((sum, item) => sum + lineCost(item), 0);
  const shortfall = Math.max(0, total - funderBudget);

  const byValue = [...items].sort(
    (a, b) => lineCost(b) - lineCost(a) || a.userMTGCardHaveId - b.userMTGCardHaveId
  );

  // Fund the most valuable cards first, but SKIP any that do not fit so cheaper
  // still-affordable cards keep their funding (see note 1 above).
  const fundedIds = new Set<number>();
  let spent = 0;
  for (const item of byValue) {
    if (spent + lineCost(item) <= funderBudget) {
      spent += lineCost(item);
      fundedIds.add(item.userMTGCardHaveId);
    }
  }

  const coveredItems = byValue.filter((i) => fundedIds.has(i.userMTGCardHaveId));
  const uncoveredItems = byValue.filter((i) => !fundedIds.has(i.userMTGCardHaveId));
  const orderedItems = [...coveredItems, ...uncoveredItems];

  if (uncoveredItems.length === 0) {
    return {
      total,
      shortfall: 0,
      orderedItems,
      uncoveredIds: [],
      coveredIds: orderedItems.map((i) => i.userMTGCardHaveId),
      spent,
    };
  }

  // The repair must be VERIFIED, not derived. Against a budget of 100, items of
  // 60/55/55 leave 115 or 110 after any single removal — still short — so no
  // repair may be offered at all.
  const clearing = items
    .filter((item) => total - lineCost(item) <= funderBudget)
    .sort((a, b) => lineCost(a) - lineCost(b) || a.userMTGCardHaveId - b.userMTGCardHaveId);
  const candidate = clearing[0];

  return {
    total,
    shortfall,
    orderedItems,
    boundaryIndex: coveredItems.length,
    coveredIds: coveredItems.map((i) => i.userMTGCardHaveId),
    uncoveredIds: uncoveredItems.map((i) => i.userMTGCardHaveId),
    spent,
    removalCandidateId: candidate?.userMTGCardHaveId,
    surplusAfterRemoval: candidate ? funderBudget - (total - lineCost(candidate)) : undefined,
  };
};
